/**
 * Manage Clubs — a club, what we know about it, and who at it has access.
 *
 * ONE CLUB, ONE PLACE. Opening a row gives you the club's details, the three
 * internal fields Priori keeps about it, and its people with their roles.
 * "Who can reach this club" is answered by looking at the club, which is the
 * thing the previous all-accounts list could not do.
 *
 * THE INTERNAL FIELDS ARE INTERNAL BY CONSTRUCTION. Primary and secondary use
 * case and notes are read and written only by /api/admin/orgs, which is
 * platform-admin only, and are deliberately absent from Organization.to_dict()
 * so no club-facing payload can pick them up by accident. The screen says so
 * rather than relying on everyone remembering.
 *
 * org_code IS NOT EDITABLE. It is written into game codes and logo paths, so
 * renaming it in a form would break references nothing here can see. The API's
 * field map refuses it too — the missing control is a courtesy, the server is
 * the rule.
 */
import { Eye, FlaskConical, Plus, Trash2, X } from 'lucide-react'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'

import type { Session } from '../lib/api'
import ClubLogo from '../components/ClubLogo'
import '../components/club-logo.css'
import { API_BASE, ApiError, api } from '../lib/api'
import { setViewAsGrant, withViewAs } from '../lib/viewAs'
import { invalidateClubsCache } from '../lib/clubsCache'
import { DESTRUCTIVE_DELETES_ENABLED } from '../lib/features'
import './admin.css'
// The scrim and confirmation panel are shared with Manage Games: one dialog
// shape for every irreversible act, so the second one a person meets already
// looks like the first.
import './manage-games.css'

type Org = {
  id: number; code: string; name: string
  league: string | null; logo_url: string | null; type: string | null
  member_count: number
  primary_use_case: string | null
  secondary_use_case: string | null
  notes: string | null
  sportmonks_team_id: number | null
  sportmonks_season_id: number | null
  created_at: string | null
  /** The club's Insights Workshop switch (app/workshop/access.py). */
  workshop?: { enabled: boolean; capabilities: string[] }
  /** Who here may run the UAT session runner (app/services/uat.py). */
  uat_users?: number[]
  /** The club's built UAT answer key — which match its session's questions
   *  are measured against (app/services/uat_keygen.py). */
  uat_key?: UatKey | null
}

/** A built UAT answer key, summarised (UatAnswerKey.summary()). */
type UatKey = {
  anchor_game_id: number
  anchor: string | null
  fingerprint: string
  generated_at: string | null
  scenarios: number
  runnable: number
  unmeasured: number
}

/** A match a UAT key could be anchored on (uat_keygen.candidates()). */
type UatCandidate = {
  game_id: number
  game_code: string
  date: string | null
  served: boolean
  direction_set: boolean
  has_video: boolean
}

/** What a club's Insights Workshop can hold — app.workshop.access.CAPABILITIES. */
const WORKSHOP_CAPS: { key: string; label: string }[] = [
  { key: 'collaborate', label: 'Analytics Collaboration' },
  { key: 'tables', label: 'Tables' },
  { key: 'ask_ai', label: 'Ask AI' },
  { key: 'dashboard', label: 'Build a dashboard' },
  { key: 'segment', label: 'Segment Workbench' },
  { key: 'attacking_structure', label: 'Attacking Structure' },
  { key: 'defensive_control', label: 'Defensive Control' },
  { key: 'provoke_pressing', label: 'Provoke Pressing' },
  { key: 'formation_dynamics', label: 'Formation Dynamics' },
]
type Member = {
  id: number; email: string; role: string | null; stored_role: string
  is_active: boolean; is_priori: boolean; last_login_at: string | null
}

const when = (iso: string | null) => (iso ? iso.replace('T', ' ').slice(0, 16) : 'never')

/* Was a local copy. Shared now, so a club's badge cannot render one way here
   and another on Manage Games — same club, two appearances, is how a list
   stops being scannable. */
function Logo({ org }: { org: Pick<Org, 'code' | 'logo_url'> & { name?: string } }) {
  return <ClubLogo code={org.code} url={org.logo_url} name={org.name} />
}

export default function ManageClubs({ session }: { session: Session }) {
  const [orgs, setOrgs] = useState<Org[] | null>(null)
  const [roles, setRoles] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [openId, setOpenId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)

  const load = useCallback(() => {
    api<{ organizations: Org[]; roles: string[] }>('/api/v1/admin/orgs')
      .then((d) => { setOrgs(d.organizations); setRoles(d.roles) })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)))
  }, [])
  useEffect(load, [load])

  if (error) {
    return (<><h1 className="page-title">Manage Clubs</h1><p className="error">{error}</p></>)
  }
  if (!orgs) return <p className="muted">Loading…</p>

  const noLogo = orgs.filter((o) => !o.logo_url).length
  const noUseCase = orgs.filter((o) => !o.primary_use_case).length

  return (
    <>
      <h1 className="page-title">Manage Clubs</h1>
      <p className="page-sub">
        {orgs.length} clubs — {noLogo} without a logo, {noUseCase} without a
        recorded use case. Open a club for its details and its people.
      </p>

      <div className="adm-bar">
        <span className="adm-bar__spacer" />
        <button className="adm-btn" onClick={() => setCreating((v) => !v)}>
          {creating ? <X size={13} /> : <Plus size={13} />}
          {creating ? 'Cancel' : 'New club'}
        </button>
      </div>

      {creating && <NewClub onDone={() => { setCreating(false); load() }} />}

      <table className="adm-table">
        <thead>
          <tr>
            <th>Club</th><th>Code</th><th>League</th>
            <th>Primary use</th><th className="adm-num">People</th>
          </tr>
        </thead>
        <tbody>
          {orgs.map((o) => (
            <Fragment key={o.id}>
              <tr className={`is-clickable${openId === o.id ? ' is-open' : ''}`}
                  tabIndex={0} role="button" aria-expanded={openId === o.id}
                  onClick={() => setOpenId(openId === o.id ? null : o.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault(); setOpenId(openId === o.id ? null : o.id)
                    }
                  }}>
                <td>
                  <span className="adm-name">
                    <Logo org={o} /><span className="adm-primary">{o.name}</span>
                  </span>
                </td>
                <td className="t-data">{o.code}</td>
                <td>{o.league ?? <span className="muted">—</span>}</td>
                <td>{o.primary_use_case ?? <span className="muted">not recorded</span>}</td>
                <td className="adm-num">{o.member_count}</td>
              </tr>
              {openId === o.id && (
                <tr className="adm-editor">
                  <td colSpan={5}>
                    <ClubPanel org={o} roles={roles} session={session}
                               onClose={() => setOpenId(null)} onChanged={load} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </>
  )
}

function ClubPanel({ org, roles, session, onClose, onChanged }: {
  org: Org; roles: string[]; session: Session
  onClose: () => void; onChanged: () => void
}) {
  const [name, setName] = useState(org.name)
  const [league, setLeague] = useState(org.league ?? '')
  const [logo, setLogo] = useState(org.logo_url ?? '')
  const [primary, setPrimary] = useState(org.primary_use_case ?? '')
  const [secondary, setSecondary] = useState(org.secondary_use_case ?? '')
  const [notes, setNotes] = useState(org.notes ?? '')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)

  const [members, setMembers] = useState<Member[] | null>(null)

  const loadMembers = useCallback(() => {
    api<{ members: Member[] }>(`/api/v1/admin/orgs/${org.id}/members`)
      .then((d) => setMembers(d.members))
      .catch(() => setMembers([]))
  }, [org.id])
  useEffect(loadMembers, [loadMembers])

  const dirty = name !== org.name
    || league !== (org.league ?? '')
    || logo !== (org.logo_url ?? '')
    || primary !== (org.primary_use_case ?? '')
    || secondary !== (org.secondary_use_case ?? '')
    || notes !== (org.notes ?? '')

  const run = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true); setMsg(null)
    try {
      await fn(); setMsg({ ok: true, text: done }); setReason(''); onChanged(); loadMembers()
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : String(e) })
    } finally { setBusy(false) }
  }

  const save = () => run(() => api(`/api/v1/admin/orgs/${org.id}`, {
    method: 'PATCH',
    body: JSON.stringify({
      org_name: name, league: league || null, logo_url: logo || null,
      primary_use_case: primary || null, secondary_use_case: secondary || null,
      notes: notes || null, reason,
    }),
  }), 'Saved.')

  /** Who here runs UAT: the people ticked in the UAT column below (Ben,
   *  2026-10-24). Each sees "Run UAT" in their club rail and the runner
   *  answers them (app/services/uat.py). */
  const uatIds = org.uat_users ?? []
  const setUat = (m: Member, on: boolean) => run(() => api(
    `/api/v1/admin/orgs/${org.id}/uat-users`, {
      method: 'POST',
      body: JSON.stringify({
        user_ids: on ? [...uatIds, m.id] : uatIds.filter((i) => i !== m.id),
        reason,
      }),
    }), on ? `${m.email} can run UAT.` : `${m.email} no longer runs UAT.`)

  /** The account a facilitated session runs as: an ENABLED member — the
   *  runner answers the person the app is acting as, so a reloaded runner
   *  tab keeps working mid-session only if they are enabled (SDET #886, B1).
   *  The reserved .test address first: sessions run as a REAL club account,
   *  never as a staff one — staff land on /status, draw four extra rail
   *  sections and see controls a customer never has — and .test cannot
   *  resolve, so it cannot collide with a person. */
  const enabled = (members ?? []).filter((m) => m.is_active && uatIds.includes(m.id))
  const uatTester = enabled.find((m) => m.email.endsWith('@priori-uat.test')) ?? enabled[0]

  /** Open the runner, then view the product as the tester.
   *
   *  ORDER MATTERS. The runner's tab is opened first, inside the click,
   *  where the popup blocker still trusts it, and pointed at the runner
   *  once the tester's grant exists: view-as is per tab (lib/viewAs), so
   *  the runner tab gets the grant in its address and this tab keeps it in
   *  its own storage.
   *
   *  WRITES ARE REFUSED while viewing as someone (authz.can, and
   *  workshop/access.require_writable 404s canvas saves). Quick and Standard
   *  need none; Comprehensive's PS-VID-01 and PS-BLD-01 do, so they are
   *  unavailable this way — say so rather than let a tester read a 404 as a
   *  missing feature. */
  const runUat = async () => {
    if (!uatTester) return
    const runner = window.open('about:blank', '_blank')
    try {
      const r = await api<{ view_as: string }>(`/api/v1/admin/impersonate/${uatTester.id}`, {
        method: 'POST',
        body: JSON.stringify({
          reason: reason || `UAT session: viewing ${org.code} as the test account`,
        }),
      })
      setViewAsGrant(r.view_as)
      if (runner) {
        runner.opener = null
        runner.location.href = withViewAs(`${API_BASE}/api/v1/uat/runner`, API_BASE)
      }
      window.location.assign('/')
    } catch (e) {
      runner?.close()
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : String(e) })
    }
  }

  const impersonate = async (m: Member) => {
    // No confirm dialog (Ben, 2026-08-29). The three things it said are all
    // still true and two are visible without it: the yellow banner names the
    // target for the whole session, and read-only is enforced in can() rather
    // than by warning anybody. It was belt-and-braces on a reversible,
    // audited, read-only act.
    try {
      // this tab's grant: only this tab views as them (lib/viewAs)
      const r = await api<{ view_as: string }>(`/api/v1/admin/impersonate/${m.id}`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason || `support: viewing ${org.code}` }),
      })
      setViewAsGrant(r.view_as)
      // Reload rather than re-render: every mounted page was rendered as the
      // real person and none of them know to refetch.
      window.location.assign('/')
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : String(e) })
    }
  }

  return (
    <div className="adm-editor__inner">
      {/* The slug beside the name, not buried in the meta line below it.
          It is the club's identity everywhere that matters — it is IMMUTABLE
          after creation and embedded in every R2 key — so somebody checking
          they are on the right club should not have to read a sentence. */}
      <h3 className="adm-editor__title">
        {org.name}
        <span className="adm-slug" title="Club slug — immutable, used in every storage path">
          {org.code}
        </span>
      </h3>
      <p className="adm-editor__hint">
        {org.sportmonks_team_id ? `Sportmonks team ${org.sportmonks_team_id}` : ''}
        {org.sportmonks_season_id ? `, season ${org.sportmonks_season_id}` : ''}
        {org.created_at
          ? `${org.sportmonks_team_id ? ' · ' : ''}added ${org.created_at.slice(0, 10)}`
          : ''}.
      </p>

      <div className="adm-row">
        <label className="adm-field">
          <span className="t-label">Name</span>
          <input className="adm-input" style={{ minWidth: 260 }}
                 value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="adm-field">
          <span className="t-label">League</span>
          <input className="adm-input" placeholder="MLS, NWSL, Serie A…"
                 value={league} onChange={(e) => setLeague(e.target.value)} />
        </label>
        <label className="adm-field" style={{ flex: '1 1 300px' }}>
          <span className="t-label">Logo URL</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input className="adm-input" style={{ flex: 1 }}
                   placeholder="/media/org-logo/xxx.png"
                   value={logo} onChange={(e) => setLogo(e.target.value)} />
            <label className="adm-btn" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Upload PNG
              <input type="file" accept="image/png" style={{ display: 'none' }}
                     onChange={async (e) => {
                       const file = e.target.files?.[0]
                       if (!file) return
                       const buf = await file.arrayBuffer()
                       const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)))
                       const code = org.code.toLowerCase()
                       try {
                         const res = await api<{ code: string; url: string }>(
                           '/api/v1/admin/team-logos',
                           { method: 'POST',
                             body: JSON.stringify({ code, data_base64: b64 }) })
                         setLogo(res.url)
                         setMsg({ ok: true, text: `Logo uploaded as ${res.code}` })
                       } catch (err) {
                         setMsg({ ok: false,
                           text: err instanceof ApiError ? err.message : String(err) })
                       }
                       e.target.value = ''
                     }} />
            </label>
          </div>
        </label>
        <Logo org={{ code: org.code, logo_url: logo || null }} />
      </div>

      <p className="adm-editor__hint adm-internal">
        Internal — visible to Priori staff only. Nobody at the club sees these.
      </p>
      <div className="adm-row">
        <label className="adm-field">
          <span className="t-label">Primary use case</span>
          <input className="adm-input" style={{ minWidth: 240 }}
                 placeholder="e.g. opposition scouting"
                 value={primary} onChange={(e) => setPrimary(e.target.value)} />
        </label>
        <label className="adm-field">
          <span className="t-label">Secondary use case</span>
          <input className="adm-input" style={{ minWidth: 240 }}
                 placeholder="e.g. player development"
                 value={secondary} onChange={(e) => setSecondary(e.target.value)} />
        </label>
      </div>
      <div className="adm-row">
        <label className="adm-field" style={{ flex: '1 1 100%' }}>
          <span className="t-label">Notes</span>
          <textarea className="adm-input adm-textarea" rows={3}
                    placeholder="Anything the next person picking up this account should know."
                    value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>

      {/* The reason owns its row and the buttons own theirs. Sharing one
          flex row put Save and Close on top of the input as soon as the
          panel was wide enough for the reason to grow under them. */}
      <div className="adm-row">
        <label className="adm-field adm-reason">
          <span className="t-label">Reason</span>
          <input className="adm-input" placeholder="why this changed"
                 value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
      </div>
      <div className="adm-actions">
        <button className="adm-btn" disabled={busy || !dirty} onClick={save}>Save</button>
        {/* Only when someone here is enabled for UAT, and only for someone
            who may impersonate. A button that explains why it cannot work is
            better than one that 404s, so the title carries the reason. */}
        {session.may_impersonate && uatTester && (
          <button className="adm-btn adm-btn--quiet" onClick={() => void runUat()}
                  title={`Open the UAT runner and view ${org.code} as ${uatTester.email}. `
                         + 'Writes are refused while viewing as someone.'}>
            <FlaskConical size={13} /> Run current UAT
          </button>
        )}
        <button className="adm-btn adm-btn--quiet" onClick={onClose}><X size={13} /> Close</button>
      </div>

      <WorkshopControl org={org} reason={reason} busy={busy}
                       onSave={(enabled, capabilities) => run(
                         () => api(`/api/v1/admin/orgs/${org.id}/workshop`, {
                           method: 'POST',
                           body: JSON.stringify({ enabled, capabilities, reason }),
                         }),
                         enabled ? 'Analysis Room is on for this club.'
                                 : 'Analysis Room is off for this club.')} />

      <UatAnchorControl org={org} reason={reason} busy={busy}
                        onBuilt={(text) => setMsg({ ok: true, text })}
                        onError={(text) => setMsg({ ok: false, text })} />

      <h4 className="adm-subhead">People at {org.code}</h4>
      <table className="adm-table">
        <thead>
          <tr><th>Person</th><th>Role</th>
            <th title="May run the UAT session runner (Run UAT in their club rail)">UAT</th>
            <th>Last sign-in</th><th /></tr>
        </thead>
        <tbody>
          {(members ?? []).map((m) => (
            <tr key={m.id}>
              <td className="adm-primary">
                {m.email}
                {m.is_priori && <span className="adm-chip adm-chip--impersonate">PRIORI</span>}
                {!m.is_active && <span className="adm-chip adm-chip--off">INACTIVE</span>}
              </td>
              <td>
                <select className="adm-select" value={m.role ?? ''}
                        disabled={busy || !reason.trim()}
                        onChange={(e) => run(() => api(
                          `/api/v1/admin/users/${m.id}/memberships/${org.id}`,
                          { method: 'PUT', body: JSON.stringify({ role: e.target.value, reason }) },
                        ), `${m.email} is now ${e.target.value}.`)}>
                  {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </td>
              <td>
                <input type="checkbox" aria-label={`${m.email} runs UAT`}
                       checked={uatIds.includes(m.id)}
                       // an inactive person can be un-ticked, never ticked: left
                       // ticked, they would silently regain UAT if reactivated
                       // (SDET review of #886)
                       disabled={busy || !reason.trim()
                                 || (!m.is_active && !uatIds.includes(m.id))}
                       onChange={(e) => void setUat(m, e.target.checked)} />
              </td>
              <td className="adm-when">{when(m.last_login_at)}</td>
              <td className="adm-num" style={{ whiteSpace: 'nowrap' }}>
                {session.may_impersonate && m.is_active && (
                  <button className="adm-btn adm-btn--quiet"
                          title="See Priori exactly as this person sees it"
                          onClick={() => void impersonate(m)}>
                    <Eye size={12} /> View as
                  </button>
                )}
                <button className="adm-btn adm-btn--danger" style={{ marginLeft: 4 }}
                        disabled={busy || !reason.trim()}
                        onClick={() => run(() => api(
                          `/api/v1/admin/users/${m.id}/memberships/${org.id}`,
                          { method: 'DELETE', body: JSON.stringify({ reason }) },
                        ), `${m.email} removed from ${org.code}.`)}>
                  Remove
                </button>
              </td>
            </tr>
          ))}
          {members !== null && members.length === 0 && (
            <tr><td colSpan={5} className="muted">Nobody has access to this club.</td></tr>
          )}
          {members === null && (
            <tr><td colSpan={5} className="muted">Loading people…</td></tr>
          )}
        </tbody>
      </table>
      {!reason.trim() && (
        <p className="adm-msg muted">
          Give a reason to enable role changes, UAT access and removals — the
          server refuses them without one.
        </p>
      )}
      {msg && <p className={`adm-msg adm-msg--${msg.ok ? 'ok' : 'err'}`}>{msg.text}</p>}

      {DESTRUCTIVE_DELETES_ENABLED && (
        <DeleteClub org={org} onDeleted={() => { onChanged(); onClose() }} />
      )}
    </div>
  )
}

/**
 * Deleting a club — the most destructive act in the product.
 *
 * WHAT IT REMOVES is spelled out before it is offered, because nobody can
 * infer it: every game and all its analytics, everything else the club owns,
 * and the accounts whose only club this was. Somebody who also belongs to
 * another club keeps their account and loses this membership; Priori staff
 * and platform admins are never removed.
 *
 * WHAT SURVIVES is said just as plainly. The audit trail keeps its record of
 * the club, and the video and tracking files in object storage are never
 * touched — which is what makes a deletion recoverable by re-importing.
 *
 * TWO DELIBERATE ACTS: the club's own code typed back, and a reason. Both are
 * enforced by the server as well, so neither is a courtesy of this dialog.
 */
function DeleteClub({ org, onDeleted }: { org: Org; onDeleted: () => void }) {
  const [open, setOpen] = useState(false)
  const [typed, setTyped] = useState('')
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const armed = typed.trim() === org.code && why.trim().length > 0

  const remove = async () => {
    setBusy(true); setErr(null)
    try {
      await api(`/api/v1/admin/orgs/${org.id}`, {
        method: 'DELETE',
        body: JSON.stringify({ reason: why.trim(), confirm_code: typed.trim() }),
      })
      invalidateClubsCache()
      setOpen(false)
      onDeleted()
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e))
    } finally { setBusy(false) }
  }

  return (
    <>
      <div className="adm-danger">
        <div>
          <span className="t-label">Delete this club</span>
          <p className="adm-note">
            Removes {org.name}, every one of its games and all their analytics,
            and the accounts whose only club this was. This cannot be undone
            from here.
          </p>
        </div>
        <button className="adm-btn adm-btn--danger" onClick={() => setOpen(true)}>
          <Trash2 size={13} /> Delete club
        </button>
      </div>

      {open && (
        <div className="mg-scrim" role="dialog" aria-modal="true"
             aria-label={`Delete ${org.name}`}>
          <div className="panel mg-confirm">
            <h3 className="mg-confirm__title">Delete {org.name}?</h3>
            <p className="adm-note">
              This removes <strong>{org.member_count} membership
              {org.member_count === 1 ? '' : 's'}</strong>, every game this club
              has and all of its analytics, and any account whose only club was
              this one. Someone who also belongs to another club keeps their
              account. Priori staff and platform admins are never removed.
            </p>
            <p className="adm-note">
              The audit trail keeps its record of this club, and the video and
              tracking files in storage are <strong>not</strong> touched — so a
              deleted club can be rebuilt by re-importing it.
            </p>
            <label className="adm-field">
              <span className="t-label">Type <code>{org.code}</code> to confirm</span>
              <input className="adm-input" value={typed} autoFocus
                     onChange={(e) => setTyped(e.target.value)} />
            </label>
            <label className="adm-field">
              <span className="t-label">Reason (required)</span>
              <input className="adm-input" value={why}
                     placeholder="why this club is being removed"
                     onChange={(e) => setWhy(e.target.value)} />
            </label>
            {err && <p className="adm-msg adm-msg--err">{err}</p>}
            <div className="mg-confirm__actions">
              <button className="adm-btn adm-btn--danger"
                      disabled={!armed || busy}
                      onClick={() => void remove()}>
                {busy ? 'Deleting…' : `Delete ${org.code} permanently`}
              </button>
              <button className="adm-btn adm-btn--quiet" disabled={busy}
                      onClick={() => setOpen(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function NewClub({ onDone }: { onDone: () => void }) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [league, setLeague] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const create = async () => {
    setBusy(true); setErr(null)
    try {
      await api('/api/v1/admin/orgs', {
        method: 'POST',
        body: JSON.stringify({ code, name, league: league || null,
                               reason: 'new club onboarded' }),
      })
      invalidateClubsCache()
      onDone()
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e))
    } finally { setBusy(false) }
  }

  return (
    <div className="panel" style={{ marginBottom: 'var(--space-6)' }}>
      <div className="adm-row" style={{ marginBottom: 0 }}>
        <label className="adm-field">
          <span className="t-label">Code</span>
          <input className="adm-input" style={{ minWidth: 110 }} placeholder="SKC"
                 value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
        </label>
        <label className="adm-field">
          <span className="t-label">Name</span>
          <input className="adm-input" style={{ minWidth: 260 }}
                 placeholder="Sporting Kansas City"
                 value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="adm-field">
          <span className="t-label">League</span>
          <input className="adm-input" placeholder="MLS" value={league}
                 onChange={(e) => setLeague(e.target.value)} />
        </label>
        <button className="adm-btn" disabled={busy || !code || !name} onClick={create}>
          Create
        </button>
      </div>
      <p className="adm-note">
        No members are added. Priori staff reach every club through platform
        scope, so a new club starts with exactly the people you put in it.
      </p>
      {err && <p className="adm-msg adm-msg--err">{err}</p>}
    </div>
  )
}


/** The club's Insights Workshop: on or off, and which parts it holds.
 *  Everyone at an enabled club sees it; Priori staff always do. Saving
 *  uses the panel's reason, and the server audits it. */
function WorkshopControl({ org, reason, busy, onSave }: {
  org: Org; reason: string; busy: boolean
  onSave: (enabled: boolean, capabilities: string[]) => void
}) {
  const current = org.workshop ?? { enabled: false, capabilities: WORKSHOP_CAPS.map((c) => c.key) }
  const [enabled, setEnabled] = useState(current.enabled)
  // never chosen (a club that was never on): offer every part, the usual pick
  const [caps, setCaps] = useState<string[]>(
    current.capabilities.length ? current.capabilities : WORKSHOP_CAPS.map((c) => c.key))
  const changed = enabled !== current.enabled
    || caps.slice().sort().join() !== current.capabilities.slice().sort().join()
  return (
    <>
      <h4 className="adm-subhead">Analysis Room</h4>
      <div className="adm-toggles">
        <label className={`adm-toggle${enabled ? ' is-on' : ''}`}>
          <input type="checkbox" checked={enabled} aria-label="Analysis Room on"
                 onChange={(e) => setEnabled(e.target.checked)} />
          On for everyone at {org.code}
        </label>
        {WORKSHOP_CAPS.map((c) => (
          <label key={c.key} className={`adm-toggle${caps.includes(c.key) ? ' is-on' : ''}`}>
            <input type="checkbox" checked={caps.includes(c.key)} disabled={!enabled}
                   onChange={(e) => setCaps((prev) => e.target.checked
                     ? [...prev, c.key] : prev.filter((k) => k !== c.key))} />
            {c.label}
          </label>
        ))}
      </div>
      <div className="adm-actions">
        <button className="adm-btn" disabled={busy || !changed || !reason.trim()}
                onClick={() => onSave(enabled, caps)}>
          Save Analysis Room
        </button>
      </div>
    </>
  )
}

/**
 * Which match this club's UAT session is measured against, and the state of
 * the key built from it (Matt, 2026-10-25).
 *
 * A UAT key is not configuration, it is a MEASUREMENT of one match: the
 * questions and their answers are generated from what that match serves
 * today. So this control shows three things a facilitator needs before a
 * session, and the second two are the ones that used to be invisible:
 *
 *   - which match the questions will be about;
 *   - how many of the 40 scenarios this club can actually be asked (a club
 *     with no film, no Workshop or one match cannot run them all);
 *   - how many checks its anchor could not answer, which are graded by hand.
 *
 * THE ANCHOR IS PINNED ONCE CHOSEN. It does not follow the newest match,
 * because a programme whose anchor moves mid-way is comparing two different
 * sets of questions and calling the result one series.
 */
function UatAnchorControl({ org, reason, busy, onBuilt, onError }: {
  org: Org; reason: string; busy: boolean
  onBuilt: (text: string) => void
  onError: (text: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [candidates, setCandidates] = useState<UatCandidate[] | null>(null)
  const [load, setLoad] = useState<'idle' | 'busy' | 'done' | 'failed'>('idle')
  const [key, setKey] = useState<UatKey | null>(org.uat_key ?? null)
  const [chosen, setChosen] = useState<number | ''>('')
  // Whether the admin actually touched the select. The body omits
  // anchor_game_id unless they did: the route reads an explicit null as
  // "unpin", and `chosen` is '' both when a club is unpinned AND when the
  // candidates GET failed before it could load the current pin. One click
  // then silently unpinned a club mid-programme (SDET review of #941).
  const [touched, setTouched] = useState(false)
  const [working, setWorking] = useState(false)

  // The parent hands these down as fresh inline arrows on every render, so
  // they cannot be in the effect's deps: calling onError sets state on the
  // parent, which re-renders, which changes their identity, which re-runs
  // the effect — one failed GET became an unbounded request loop against
  // the admin API (SDET review of #941, finding 1). A ref keeps the latest
  // callback without making it a dependency.
  const notify = useRef({ onBuilt, onError })
  notify.current = { onBuilt, onError }

  // Candidates are a per-club read; only pay for it when someone looks,
  // and only once — `load` makes a failure terminal rather than a retry.
  useEffect(() => {
    if (!open || load !== 'idle') return
    setLoad('busy')
    void (async () => {
      try {
        const got = await api<{ candidates: UatCandidate[]; key: UatKey | null
          anchor_game_id: number | null }>(`/api/v1/admin/orgs/${org.id}/uat-key`)
        setCandidates(got.candidates)
        setKey(got.key)
        setChosen(got.anchor_game_id ?? '')
        setLoad('done')
      } catch (e) {
        setLoad('failed')
        notify.current.onError(e instanceof ApiError ? e.message : String(e))
      }
    })()
  }, [open, load, org.id])

  const build = async () => {
    setWorking(true)
    try {
      const got = await api<{ key: UatKey }>(`/api/v1/admin/orgs/${org.id}/uat-key`, {
        method: 'POST',
        body: JSON.stringify({
          ...(touched ? { anchor_game_id: chosen === '' ? null : chosen } : {}),
          reason,
        }),
      })
      setKey(got.key)
      notify.current.onBuilt(`UAT key built for ${org.code}: ${got.key.runnable} of `
        + `${got.key.scenarios} scenarios runnable.`)
    } catch (e) {
      notify.current.onError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setWorking(false)
    }
  }

  return (
    <>
      <h4 className="adm-subhead">
        UAT anchor match
        <button className="adm-btn adm-btn--quiet" style={{ marginLeft: 10 }}
                onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide' : 'Show'}
        </button>
      </h4>
      <p className="adm-note">
        {key
          ? `${key.anchor ?? `game ${key.anchor_game_id}`} · ${key.runnable} of `
            + `${key.scenarios} scenarios runnable`
            + (key.unmeasured ? ` · ${key.unmeasured} checks graded by hand` : '')
            + ` · built ${key.generated_at?.slice(0, 16).replace('T', ' ') ?? '—'}`
            + ` · ${key.fingerprint.slice(0, 12)}`
          : 'No key built. Until one is, nobody here can run a session — the runner '
            + 'says so rather than asking another club’s questions.'}
      </p>
      {open && (
        <>
          <div className="adm-toggles">
            <label className="adm-field">
              <span className="t-label">Anchor match</span>
              <select className="adm-select" value={chosen}
                      disabled={load !== 'done'}
                      onChange={(e) => {
                        setTouched(true)
                        setChosen(e.target.value === '' ? ''
                          : Number(e.target.value))
                      }}>
                <option value="">Best available (not pinned)</option>
                {load === 'failed' && <option disabled>could not load matches</option>}
                {(candidates ?? []).map((c) => (
                  <option key={c.game_id} value={c.game_id} disabled={!c.served}>
                    {c.game_code}{c.date ? ` · ${c.date}` : ''}
                    {c.served ? '' : ' · not on the stack'}
                    {c.served && !c.direction_set ? ' · attack direction unset' : ''}
                    {c.served && !c.has_video ? ' · no film' : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="adm-actions">
            <button className="adm-btn"
                    disabled={busy || working || !reason.trim() || load !== 'done'}
                    onClick={() => void build()}
                    title="Measures this club's anchor and rebuilds its questions. Takes a
                           few seconds.">
              {working ? 'Measuring…' : 'Build key'}
            </button>
          </div>
        </>
      )}
    </>
  )
}

